import { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

import { useQuery } from '@apollo/client/react'

import {
  ArrowLeftIcon,
  Button,
  Heading,
  Link,
  Text,
} from '@buffer-mono/popcorn'

import { CollaborationNotesProvider } from '~publish/components/CollaborationNotes'
import { useCollaborationNotesContext } from '~publish/components/CollaborationNotes'
import { NotesFeed } from '~publish/components/Notes'
import { PostNote } from '~publish/components/PostNotes'
import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { ContentItemNotesSummaryQuery } from '~publish/hooks/notes/contentItemNotesSummary'
import { contentItemNoteSubject } from '~publish/hooks/notes/noteSubject'
import type { NotesFailure } from '~publish/hooks/notes/useNotes'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'

import { contentItemNotesAdapter } from './contentItemNotesAdapter'

import styles from './ContentItemNotes.module.css'

export const ARCHIVE_HEADING = 'Draft notes (archived)'
export const ARCHIVE_INTRO = 'Notes from before this content was scheduled.'
export const VIEW_DRAFT_NOTES = 'View draft notes'
export const BACK_TO_POST_NOTES = 'Back to Post notes'

const FAILURE_COPY: Record<NotesFailure, string> = {
  permissionDenied: 'You do not have permission to view draft notes.',
  unavailable: 'Draft notes are unavailable.',
  requestFailed: 'Draft notes could not load.',
}

/**
 * Offers the closed draft thread from a Post's Notes panel, only when it
 * holds Notes. Mount it only in an open panel: it reads the ContentItem's
 * summary once, from the cache when a sibling Post already read it.
 *
 * `returnedFromArchive` gives it focus back when a reader leaves the archive,
 * and keeps it even if they deleted the archive's last Note, so focus has
 * somewhere to land.
 */
export function ViewDraftNotesButton({
  contentItemId,
  returnedFromArchive = false,
  onOpen,
}: {
  contentItemId: string
  returnedFromArchive?: boolean
  onOpen: () => void
}): JSX.Element | null {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const focusOnMount = useRef(returnedFromArchive)
  const { data } = useQuery(ContentItemNotesSummaryQuery, {
    variables: { contentItemId },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })
  const totalCount = data?.contentItem?.notesSummary?.totalCount ?? 0
  const visible = totalCount > 0 || returnedFromArchive

  useEffect(() => {
    if (!visible || !focusOnMount.current) return
    focusOnMount.current = false
    buttonRef.current?.focus()
  }, [visible])

  if (!visible) return null

  return (
    <div className={styles.viewArchive}>
      <Button
        ref={buttonRef}
        type="button"
        size="small"
        variant="secondary"
        onClick={onOpen}
      >
        {VIEW_DRAFT_NOTES}
      </Button>
    </div>
  )
}

/**
 * The closed thread, newest first. It is the view's only scroll container
 * and pages down from its own sentinel.
 */
function ArchivedNotesFeed(): JSX.Element {
  const currentAccountId = useAccountId()
  const { notes: result } = useCollaborationNotesContext()
  const ready = result.status === 'ready' ? result : null
  const [sentinelRef] = useInfiniteScrollPagination({
    loading: Boolean(
      !ready || ready.fetchingMore || ready.refreshing || ready.pageError,
    ),
    hasNextPage: ready?.hasNextPage,
    fetchMore: ready?.loadMore ?? ((): Promise<void> => Promise.resolve()),
  })

  if (result.status === 'failed') {
    return (
      <NotesFeed.Status className={styles.archiveStatus}>
        <span>{FAILURE_COPY[result.reason]}</span>{' '}
        <Link as="button" type="button" onClick={() => void result.retry()}>
          Try again
        </Link>
      </NotesFeed.Status>
    )
  }
  if (!ready) {
    return (
      <NotesFeed.Status className={styles.archiveStatus}>
        Loading draft notes…
      </NotesFeed.Status>
    )
  }
  if (ready.notes.length === 0 && !ready.hasNextPage) {
    return (
      <NotesFeed.Status className={styles.archiveStatus}>
        No draft notes.
      </NotesFeed.Status>
    )
  }

  return (
    <div className={styles.archiveScroll} data-testid="draft-notes-scroll">
      <NotesFeed.List className={styles.archiveList}>
        {ready.notes.map((note) => (
          <PostNote
            key={note.id}
            note={note}
            currentAccountId={currentAccountId}
          />
        ))}
      </NotesFeed.List>
      {ready.fetchingMore && (
        <NotesFeed.Status className={styles.archiveStatus}>
          Loading more draft notes…
        </NotesFeed.Status>
      )}
      {ready.pageError && !ready.fetchingMore && (
        <NotesFeed.Status className={styles.archiveStatus}>
          <span>More draft notes could not load.</span>{' '}
          <Link as="button" type="button" onClick={() => void ready.loadMore()}>
            Retry loading more
          </Link>
        </NotesFeed.Status>
      )}
      {ready.hasNextPage && (
        <div
          ref={sentinelRef}
          className={styles.sentinel}
          data-testid="draft-notes-pagination-sentinel"
          aria-hidden="true"
        />
      )}
    </div>
  )
}

/**
 * The Notes a ContentItem gathered as a draft, shown in place of a Post's
 * feed. The thread is closed: Notes keep their edit, delete, and reaction
 * controls, and there is no composer. The draft text itself is not shown.
 *
 * Focus moves to the heading when the view opens. `onBack` adds the control
 * that returns to the Post feed; a host with no Post feed leaves it out and
 * may pass `children`, such as links to the Posts, below the intro.
 */
export function DraftNotesArchive({
  contentItemId,
  onBack,
  children,
}: {
  contentItemId: string
  onBack?: () => void
  children?: ReactNode
}): JSX.Element {
  const organizationId = useOrganizationId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const headingId = useId()
  const subject = useMemo(
    () => contentItemNoteSubject(contentItemId),
    [contentItemId],
  )
  const mentionScope = useMemo(
    () => (organizationId ? { organizationId } : null),
    [organizationId],
  )
  const focusHeading = useCallback((): void => {
    headingRef.current?.focus()
  }, [])

  useEffect(() => {
    focusHeading()
  }, [focusHeading])

  return (
    <section aria-labelledby={headingId} className={styles.archive}>
      <div className={styles.archiveHeader}>
        {onBack && (
          <Button
            type="button"
            size="small"
            variant="tertiary"
            className={styles.archiveBack}
            onClick={onBack}
          >
            <ArrowLeftIcon /> {BACK_TO_POST_NOTES}
          </Button>
        )}
        <Heading
          ref={headingRef}
          id={headingId}
          as="h3"
          size="small"
          tabIndex={-1}
          className={styles.archiveHeading}
        >
          {ARCHIVE_HEADING}
        </Heading>
        <Text as="p" size="sm" color="subtle">
          {ARCHIVE_INTRO}
        </Text>
        {children}
      </div>
      <CollaborationNotesProvider
        subject={subject}
        adapter={contentItemNotesAdapter}
        canCreate={false}
        isClosed
        focusAfterDelete={focusHeading}
        mentionScope={mentionScope}
      >
        <ArchivedNotesFeed />
      </CollaborationNotesProvider>
    </section>
  )
}
